import {
  useEffect,
  useState,
  useContext,
  createContext,
  ReactNode,
} from "react";
import moment from "moment-timezone";
import styled from "styled-components/macro";
import { isImpersonating } from "../api";
import Portal from "../components/Portal";
import {
  BoxContent,
  Box,
  BoxHeader,
  BoxFooter,
  BoxNavLeft,
  BoxNavRight,
  BoxTitle,
} from "../components/Box";
import Button from "../components/Button";
import Stack from "../components/Stack";
import { useQuery } from "@tanstack/react-query";
import { fetchUser } from "../api/users";

type Moment = typeof moment;
type Context = { moment: Moment };

export const context = createContext<Context | null>(null);

export { moment };

export default function useMoment() {
  const wrapper = useContext(context) || { moment };
  return wrapper.moment;
}

export function MomentProvider({ children }: { children: ReactNode }) {
  const user = useQuery({
    queryKey: ["users", "me"],
    queryFn: fetchUser,
    select: (queryData) => queryData.data,
  }).data!;
  const [isShowing, setIsShowing] = useState(false);
  const [hasConfirmed, setHasConfirmed] = useState(false);

  // moment is wrapped in an object
  // so it's a different reference when it is set
  const [momentWrapper, setMomentWrapper] = useState({ moment });

  useEffect(() => {
    if (hasConfirmed) return;
    if (
      moment().utcOffset() !== moment().tz(user.standardTimeZone).utcOffset()
    ) {
      // impersonators will always use the account timezone
      if (isImpersonating() || !user.isTrueUser) {
        moment.tz.setDefault(user.standardTimeZone);
        setMomentWrapper({ moment });
      } else {
        // local timezone is not the user's timezone.
        setIsShowing(true);
      }
    }
  }, [user, hasConfirmed]);

  return (
    <context.Provider value={momentWrapper}>
      {children}
      {isShowing && (
        <Portal>
          <Wrapper>
            <Container>
              <Box elevated>
                <BoxHeader>
                  <BoxTitle>Which time zone should we use?</BoxTitle>
                </BoxHeader>
                <BoxContent>
                  <Stack>
                    <HelpParagraph>
                      We have detected that your current time zone is different
                      than the one chosen in your profile settings. Would you
                      like to view dates and times according to the current
                      local time or the chosen time in your settings?
                    </HelpParagraph>
                  </Stack>
                </BoxContent>
                <BoxFooter white stacked>
                  <BoxNavLeft>
                    <Button
                      variant="link"
                      onClick={() => {
                        setHasConfirmed(true);
                        setIsShowing(false);
                      }}
                    >
                      Use local timezone
                    </Button>
                  </BoxNavLeft>
                  <BoxNavRight>
                    <Button
                      onClick={() => {
                        setHasConfirmed(true);
                        setIsShowing(false);
                        moment.tz.setDefault(user.standardTimeZone);
                        setMomentWrapper({ moment });
                      }}
                    >
                      Use {user.timeZone}
                    </Button>
                  </BoxNavRight>
                </BoxFooter>
              </Box>
            </Container>
          </Wrapper>
        </Portal>
      )}
    </context.Provider>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  height: 100%;
  overflow: auto;
  z-index: 10000;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #0001;
`;

const Container = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 40px;
  width: 60vw;
  max-height: 100%;
  max-width: 600px;
`;

const HelpParagraph = styled.p`
  max-width: 70ch;
  line-height: 1.6em;
  strong {
    font-weight: 500;
  }
`;
